import React, { createContext, useState, useContext, useRef } from 'react';

/**
 * Context for managing try-on session state across components
 */
export const TryOnContext = createContext({
  currentStep: 1,
  setCurrentStep: () => {},
  sessionId: null,
  setSessionId: () => {},
  capturedImageUrl: null,
  setCapturedImageUrl: () => {},
  tryOnResults: [],
  setTryOnResults: () => {},
  selectedResultIndex: 0,
  setSelectedResultIndex: () => {},
  retrievedSession: null,
  setRetrievedSession: () => {},
  resetSession: () => {},
  requestReset: () => {},
  // Background upload tracking
  uploadPromise: null,
  setUploadPromise: () => {},
  capturedBlob: null,
  setCapturedBlob: () => {}
});

/**
 * Hook to use the TryOnContext
 */
export const useTryOn = () => {
  const context = useContext(TryOnContext);
  if (!context) {
    throw new Error('useTryOn must be used within a TryOnProvider');
  }
  return context;
};

/**
 * Provider component for try-on state
 */
export function TryOnProvider({ children }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [sessionId, setSessionId] = useState(null);
  const [capturedImageUrl, setCapturedImageUrl] = useState(null);
  const [tryOnResults, setTryOnResults] = useState([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState(0);
  const [retrievedSession, setRetrievedSession] = useState(null);
  const [waitingForPhoto, setWaitingForPhoto] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  // Background upload tracking - use ref to avoid re-renders
  const uploadPromiseRef = useRef(null);
  const tryOnPromiseRef = useRef(null);
  const tryOnSettledRef = useRef(false);
  const [capturedBlob, setCapturedBlob] = useState(null);

  const setUploadPromise = (promise) => {
    uploadPromiseRef.current = promise;
  };

  const getUploadPromise = () => uploadPromiseRef.current;

  const setTryOnPromise = (promise) => {
    tryOnPromiseRef.current = promise;
    tryOnSettledRef.current = false;
    if (promise && typeof promise.then === 'function') {
      promise.then(
        () => { tryOnSettledRef.current = true; },
        () => { tryOnSettledRef.current = true; }
      );
    }
  };

  const getTryOnPromise = () => tryOnPromiseRef.current;
  const isTryOnSettled = () => tryOnSettledRef.current;

  /**
   * Reset all session state and return to step 1
   */
  const resetSession = () => {
    setCurrentStep(1);
    setSessionId(null);
    setCapturedImageUrl(null);
    setTryOnResults([]);
    setSelectedResultIndex(0);
    setRetrievedSession(null);
    setWaitingForPhoto(false);
    uploadPromiseRef.current = null;
    tryOnPromiseRef.current = null;
    tryOnSettledRef.current = false;
    setCapturedBlob(null);
  };

  const requestReset = () => setResetConfirmOpen(true);
  const cancelReset = () => setResetConfirmOpen(false);
  const confirmReset = () => {
    setResetConfirmOpen(false);
    resetSession();
  };

  const value = {
    currentStep,
    setCurrentStep,
    sessionId,
    setSessionId,
    capturedImageUrl,
    setCapturedImageUrl,
    tryOnResults,
    setTryOnResults,
    selectedResultIndex,
    setSelectedResultIndex,
    retrievedSession,
    setRetrievedSession,
    waitingForPhoto,
    setWaitingForPhoto,
    resetSession,
    requestReset,
    // Background upload
    setUploadPromise,
    getUploadPromise,
    setTryOnPromise,
    getTryOnPromise,
    isTryOnSettled,
    capturedBlob,
    setCapturedBlob
  };

  return (
    <TryOnContext.Provider value={value}>
      {children}
      {resetConfirmOpen && (
        <div className="reset-modal-backdrop" role="dialog" aria-modal="true">
          <div className="reset-modal">
            <h2 className="reset-modal-title">Are you sure?</h2>
            <p className="reset-modal-body">All progress will be lost.</p>
            <div className="reset-modal-actions">
              <button className="reset-modal-btn reset-modal-btn-no" onClick={cancelReset}>
                No
              </button>
              <button className="reset-modal-btn reset-modal-btn-yes" onClick={confirmReset}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </TryOnContext.Provider>
  );
}

export default TryOnContext;
